import {
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal } from 'antd';
import { createRoom, openRoom } from 'services/api/appointments';

import { showRestError } from 'utils/showErrorMessage';
import { Loading } from 'components/ui';
import { CopyLinkPassword } from 'components/shared';

export type CreateVideoCallModalRef = {
  onAppointmentCreated(hashId: string): void;
};

interface IProps {
  onClose(): void;
  onCreate(): void;
  onCreateAppointment(notes: string): void;
}

const CreateVideoCallModal: ForwardRefRenderFunction<
  CreateVideoCallModalRef,
  IProps
> = ({ onClose, onCreate, onCreateAppointment }, ref) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [step, setStep] = useState(1);
  const [isLoading, setLoading] = useState(false);
  const [link, setLink] = useState('');
  const [password, setPassword] = useState('');

  const openRoomFor = (hashId: string) => {
    openRoom(hashId)
      .then(room => {
        // shared meeting URL keys off the appointment hashId (falls back to
        // the room meetUrl — same encrypted id — if hashId is absent)
        const roomHash = hashId || room.meetUrl;

        setLink(`${room.redirect_url}/home/${roomHash}?creator=doctor`);

        return createRoom(hashId);
      })
      .then(created => {
        onCreate();
        setStep(2);
        setPassword(created.password);
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  };

  const renderModalContent = () => {
    if (isLoading) {
      return <Loading type="static" />;
    }

    switch (step) {
      case 1:
        return (
          <Form layout="vertical" form={form}>
            <Form.Item
              name="notes"
              label={t('video_call.note')}
              tooltip={t('video_call.note_only_for_you')}
            >
              <Input.TextArea
                placeholder={t('video_call.note')}
                style={{ minHeight: 127 }}
              />
            </Form.Item>
          </Form>
        );
      case 2:
        return <CopyLinkPassword link={link} password={password} />;
      default:
        return null;
    }
  };

  const onClickOk = () => {
    switch (step) {
      case 1: {
        setLoading(true);
        const notes = form.getFieldValue('notes');

        onCreateAppointment(notes);
        break;
      }

      default:
        onClose();
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    onAppointmentCreated: hashId => {
      openRoomFor(hashId);
    }
  }));

  return (
    <Modal
      title={t('video_call.create_new')}
      onCancel={onClose}
      onOk={onClickOk}
      cancelText={t('common.cancel')}
      cancelButtonProps={{
        disabled: isLoading
      }}
      okButtonProps={{
        disabled: isLoading
      }}
      closable={!isLoading}
      open
    >
      {renderModalContent()}
    </Modal>
  );
};

export default forwardRef(CreateVideoCallModal);
